import { createFileRoute, useNavigate } from '@tanstack/react-router';
import { useMemo, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import type { ColumnDef } from '@tanstack/react-table';
import clsx from 'clsx';
import { Download } from 'lucide-react';
import { apiRequest, ApiError, type ListResponse, openApiDocument } from '../lib/api';
import { useToast } from '../lib/toast';
import { formatDate, formatINR } from '../lib/format';
import { ListShell } from '../components/ListShell';
import { ListOrOutlet } from '../components/shared/ListOrOutlet';
import { DataTable } from '../components/ui/DataTable';
import { EmptyState } from '../components/ui/EmptyState';
import { ErrorState } from '../components/ui/ErrorState';
import { Button } from '../components/ui/Button';
import {
  cycleShortLabel,
  type FeePaymentJoined,
  type SalaryPaymentJoined,
} from '../components/finance/types';

export const Route = createFileRoute('/_authed/finance/receipts')({
  component: ReceiptsPage,
});

type Tab = 'receipts' | 'payslips';

function ReceiptsPage() {
  const [tab, setTab] = useState<Tab>('receipts');
  const [search, setSearch] = useState('');
  const { toast } = useToast();
  const navigate = useNavigate();

  const openSignedUrl = async (path: string) => {
    try {
      await openApiDocument(path);
    } catch (err) {
      if (err instanceof ApiError) toast(err.message, { variant: 'error' });
      else toast('Could not open document', { variant: 'error' });
    }
  };

  const onReceiptRowClick = (row: FeePaymentJoined): void => {
    void navigate({
      to: '/finance/receipts/$paymentId',
      params: { paymentId: row.payment.id },
    });
  };

  const onPayslipRowClick = (row: SalaryPaymentJoined): void => {
    void navigate({
      to: '/finance/payslips/$paymentId',
      params: { paymentId: row.payment.id },
    });
  };

  return (
    <ListOrOutlet basePath="/finance/receipts">
    <ListShell
      title="Receipts & payslips"
      description="View and download paid records."
      search={{
        value: search,
        onChange: setSearch,
        placeholder:
          tab === 'receipts'
            ? 'Search by student name or cycle (YYYY-MM)'
            : 'Search by payee name or cycle (YYYY-MM)',
      }}
    >
      <div role="tablist" aria-label="Document type" className="flex gap-1 border-b border-[color:var(--color-border)] mb-3">
        <TabButton
          active={tab === 'receipts'}
          onClick={() => setTab('receipts')}
          id="tab-receipts"
          ariaControls="panel-receipts"
        >
          Receipts
        </TabButton>
        <TabButton
          active={tab === 'payslips'}
          onClick={() => setTab('payslips')}
          id="tab-payslips"
          ariaControls="panel-payslips"
        >
          Payslips
        </TabButton>
      </div>

      {tab === 'receipts' ? (
        <ReceiptsTab
          search={search}
          onDownload={(id) => openSignedUrl(`/api/fees/payments/${encodeURIComponent(id)}/receipt-url`)}
          onRowClick={onReceiptRowClick}
        />
      ) : (
        <PayslipsTab
          search={search}
          onDownload={(id) => openSignedUrl(`/api/salaries/payments/${encodeURIComponent(id)}/payslip-url`)}
          onRowClick={onPayslipRowClick}
        />
      )}
    </ListShell>
    </ListOrOutlet>
  );
}

interface TabButtonProps {
  active: boolean;
  onClick: () => void;
  id: string;
  ariaControls: string;
  children: React.ReactNode;
}

const TabButton = ({ active, onClick, id, ariaControls, children }: TabButtonProps) => (
  <button
    type="button"
    role="tab"
    id={id}
    aria-controls={ariaControls}
    aria-selected={active}
    onClick={onClick}
    className={clsx(
      'px-4 py-2 text-sm font-medium border-b-2 -mb-px transition-colors',
      active
        ? 'border-gold-500 text-[color:var(--color-text)]'
        : 'border-transparent text-[color:var(--color-text-muted)] hover:text-[color:var(--color-text)]',
    )}
  >
    {children}
  </button>
);

// ---------- Receipts tab ----------

interface ReceiptRowExtras {
  cycleYear?: number;
  cycleMonth?: number;
}

type ReceiptRow = FeePaymentJoined & ReceiptRowExtras;

interface ReceiptsTabProps {
  search: string;
  onDownload: (paymentId: string) => void;
  onRowClick: (row: FeePaymentJoined) => void;
}

function ReceiptsTab({ search, onDownload, onRowClick }: ReceiptsTabProps) {
  const query = useQuery({
    queryKey: ['feePayments', null, 'paid'] as const,
    queryFn: async ({ signal }) => {
      const res = await apiRequest<ListResponse<FeePaymentJoined> | { items: unknown[] }>(
        '/api/fees/payments',
        { signal, query: { status: 'paid' } },
      );
      return (res.items ?? []).map(normaliseFeeRow) as ReceiptRow[];
    },
  });

  const filtered = useMemo(() => {
    const rows = query.data ?? [];
    const q = search.trim().toLowerCase();
    if (!q) return rows;
    return rows.filter((r) => {
      const name = r.student.name.toLowerCase();
      const cycleStr =
        r.cycleYear && r.cycleMonth
          ? cycleShortLabel(r.cycleYear, r.cycleMonth).toLowerCase()
          : '';
      return (
        name.includes(q) ||
        cycleStr.includes(q) ||
        (r.payment.receiptNumber?.toLowerCase().includes(q) ?? false)
      );
    });
  }, [query.data, search]);

  const columns = useMemo<ColumnDef<ReceiptRow, unknown>[]>(
    () => [
      {
        id: 'cycle',
        header: 'Cycle',
        accessorFn: (r) => (r.cycleYear && r.cycleMonth ? cycleShortLabel(r.cycleYear, r.cycleMonth) : ''),
        cell: ({ row }) =>
          row.original.cycleYear && row.original.cycleMonth
            ? cycleShortLabel(row.original.cycleYear, row.original.cycleMonth)
            : '—',
      },
      {
        id: 'student',
        header: 'Student',
        accessorFn: (r) => r.student.name,
        cell: ({ row }) => row.original.student.name,
      },
      {
        id: 'amount',
        header: 'Amount',
        accessorFn: (r) => r.payment.netAmountPaise,
        cell: ({ row }) => formatINR(row.original.payment.netAmountPaise),
      },
      {
        id: 'paidAt',
        header: 'Paid on',
        accessorFn: (r) => r.payment.paidAt ?? '',
        cell: ({ row }) =>
          row.original.payment.paidAt
            ? formatDate(row.original.payment.paidAt, { withTime: true })
            : '—',
      },
      {
        id: 'receiptNumber',
        header: 'Receipt #',
        cell: ({ row }) =>
          row.original.payment.receiptNumber ? (
            <span className="font-mono text-xs">{row.original.payment.receiptNumber}</span>
          ) : (
            <span className="text-xs text-[color:var(--color-text-muted)]">pending</span>
          ),
      },
      {
        id: 'download',
        header: '',
        enableSorting: false,
        cell: ({ row }) => (
          <Button
            size="sm"
            variant="secondary"
            icon={<Download size={14} />}
            onClick={(e) => {
              e.stopPropagation();
              onDownload(row.original.payment.id);
            }}
          >
            Download
          </Button>
        ),
      },
    ],
    [onDownload],
  );

  if (query.error) {
    return (
      <ErrorState
        title="Could not load receipts"
        description={
          query.error instanceof ApiError
            ? query.error.message
            : 'Please try again.'
        }
        onRetry={() => query.refetch()}
      />
    );
  }

  return (
    <div role="tabpanel" id="panel-receipts" aria-labelledby="tab-receipts">
      <DataTable<ReceiptRow>
        data={filtered}
        columns={columns}
        isLoading={query.isLoading}
        getRowId={(r) => r.payment.id}
        onRowClick={onRowClick}
        emptyState={
          <EmptyState
            title="No receipts found"
            description={
              search
                ? 'Try a different search term.'
                : 'Paid fee payments will appear here.'
            }
          />
        }
      />
    </div>
  );
}

// ---------- Payslips tab ----------

interface PayslipRowExtras {
  cycleYear?: number;
  cycleMonth?: number;
}
type PayslipRow = SalaryPaymentJoined & PayslipRowExtras;

interface PayslipsTabProps {
  search: string;
  onDownload: (paymentId: string) => void;
  onRowClick: (row: SalaryPaymentJoined) => void;
}

function PayslipsTab({ search, onDownload, onRowClick }: PayslipsTabProps) {
  const query = useQuery({
    queryKey: ['salaryPayments', null, 'paid'] as const,
    queryFn: async ({ signal }) => {
      const res = await apiRequest<ListResponse<SalaryPaymentJoined> | { items: unknown[] }>(
        '/api/salaries/payments',
        { signal, query: { status: 'paid' } },
      );
      return (res.items ?? []).map(normaliseSalaryRow) as PayslipRow[];
    },
  });

  const filtered = useMemo(() => {
    const rows = query.data ?? [];
    const q = search.trim().toLowerCase();
    if (!q) return rows;
    return rows.filter((r) => {
      const name = r.payee.name.toLowerCase();
      const cycleStr =
        r.cycleYear && r.cycleMonth
          ? cycleShortLabel(r.cycleYear, r.cycleMonth).toLowerCase()
          : '';
      return (
        name.includes(q) ||
        cycleStr.includes(q) ||
        (r.payment.payslipNumber?.toLowerCase().includes(q) ?? false)
      );
    });
  }, [query.data, search]);

  const columns = useMemo<ColumnDef<PayslipRow, unknown>[]>(
    () => [
      {
        id: 'cycle',
        header: 'Cycle',
        accessorFn: (r) =>
          r.cycleYear && r.cycleMonth ? cycleShortLabel(r.cycleYear, r.cycleMonth) : '',
        cell: ({ row }) =>
          row.original.cycleYear && row.original.cycleMonth
            ? cycleShortLabel(row.original.cycleYear, row.original.cycleMonth)
            : '—',
      },
      {
        id: 'payee',
        header: 'Payee',
        accessorFn: (r) => r.payee.name,
        cell: ({ row }) => (
          <div className="min-w-0">
            <div className="font-medium truncate">{row.original.payee.name}</div>
            <div className="text-xs capitalize text-[color:var(--color-text-muted)]">
              {row.original.payee.role}
            </div>
          </div>
        ),
      },
      {
        id: 'amount',
        header: 'Amount',
        accessorFn: (r) => r.payment.netPaise,
        cell: ({ row }) => formatINR(row.original.payment.netPaise),
      },
      {
        id: 'paidAt',
        header: 'Paid on',
        accessorFn: (r) => r.payment.paidAt ?? '',
        cell: ({ row }) =>
          row.original.payment.paidAt
            ? formatDate(row.original.payment.paidAt, { withTime: true })
            : '—',
      },
      {
        id: 'payslipNumber',
        header: 'Payslip #',
        cell: ({ row }) =>
          row.original.payment.payslipNumber ? (
            <span className="font-mono text-xs">{row.original.payment.payslipNumber}</span>
          ) : (
            <span className="text-xs text-[color:var(--color-text-muted)]">pending</span>
          ),
      },
      {
        id: 'download',
        header: '',
        enableSorting: false,
        cell: ({ row }) => (
          <Button
            size="sm"
            variant="secondary"
            icon={<Download size={14} />}
            onClick={(e) => {
              e.stopPropagation();
              onDownload(row.original.payment.id);
            }}
          >
            Download
          </Button>
        ),
      },
    ],
    [onDownload],
  );

  if (query.error) {
    return (
      <ErrorState
        title="Could not load payslips"
        description={
          query.error instanceof ApiError
            ? query.error.message
            : 'Please try again.'
        }
        onRetry={() => query.refetch()}
      />
    );
  }

  return (
    <div role="tabpanel" id="panel-payslips" aria-labelledby="tab-payslips">
      <DataTable<PayslipRow>
        data={filtered}
        columns={columns}
        isLoading={query.isLoading}
        getRowId={(r) => r.payment.id}
        onRowClick={onRowClick}
        emptyState={
          <EmptyState
            title="No payslips found"
            description={
              search
                ? 'Try a different search term.'
                : 'Paid salaries will appear here.'
            }
          />
        }
      />
    </div>
  );
}

// ---------- Normalisers (copy of the per-route helpers, kept inline to avoid
// importing across route boundaries) ----------

function normaliseFeeRow(raw: unknown): ReceiptRow {
  const base = raw as Record<string, unknown>;
  if (base && typeof base === 'object' && 'payment' in base && 'student' in base) {
    const joined = raw as FeePaymentJoined & { cycle?: { year?: number; month?: number } };
    const extras: ReceiptRowExtras = {};
    if (joined.cycle?.year !== undefined) extras.cycleYear = joined.cycle.year;
    if (joined.cycle?.month !== undefined) extras.cycleMonth = joined.cycle.month;
    return { ...joined, ...extras };
  }
  // Legacy raw row fallback (no joined student) — best-effort.
  const payment = {
    id: String(base.id ?? ''),
    cycleId: String(base.cycleId ?? base.cycle_id ?? ''),
    studentId: String(base.studentId ?? base.student_id ?? ''),
    amountPaise: Number(base.amountPaise ?? base.amount_paise ?? 0),
    discountPaise: Number(base.discountPaise ?? base.discount_paise ?? 0),
    netAmountPaise: Number(base.netAmountPaise ?? base.net_amount_paise ?? 0),
    dueDate: String(base.dueDate ?? base.due_date ?? ''),
    status: (base.status as FeePaymentJoined['payment']['status']) ?? 'paid',
    paidAt: (base.paidAt ?? base.paid_at ?? null) as string | null,
    paidVia: (base.paidVia ?? base.paid_via ?? null) as FeePaymentJoined['payment']['paidVia'],
    paidReference: (base.paidReference ?? base.paid_reference ?? null) as string | null,
    receiptNumber: (base.receiptNumber ?? base.receipt_number ?? null) as string | null,
    provider:
      (base.provider as FeePaymentJoined['payment']['provider']) ?? 'manual',
    providerOrderId: (base.providerOrderId ?? base.provider_order_id ?? null) as string | null,
    providerPaymentId: (base.providerPaymentId ?? base.provider_payment_id ?? null) as
      | string
      | null,
    providerSignature: (base.providerSignature ?? base.provider_signature ?? null) as
      | string
      | null,
    providerPaidAt: (base.providerPaidAt ?? base.provider_paid_at ?? null) as string | null,
    notes: (base.notes ?? null) as string | null,
    confirmedBy: (base.confirmedBy ?? base.confirmed_by ?? null) as string | null,
    createdAt: String(base.createdAt ?? base.created_at ?? ''),
    updatedAt: String(base.updatedAt ?? base.updated_at ?? ''),
  };
  return {
    payment,
    student: {
      id: payment.studentId,
      name: String(base.studentName ?? base.student_name ?? '—'),
      admissionNumber: (base.admissionNumber ?? base.admission_number ?? null) as
        | string
        | null,
      householdId: (base.householdId ?? base.household_id ?? null) as string | null,
    },
  };
}

function normaliseSalaryRow(raw: unknown): PayslipRow {
  const base = raw as Record<string, unknown>;
  if (base && typeof base === 'object' && 'payment' in base && 'payee' in base) {
    const joined = raw as SalaryPaymentJoined & { cycle?: { year?: number; month?: number } };
    const extras: PayslipRowExtras = {};
    if (joined.cycle?.year !== undefined) extras.cycleYear = joined.cycle.year;
    if (joined.cycle?.month !== undefined) extras.cycleMonth = joined.cycle.month;
    return { ...joined, ...extras };
  }
  const role = (base.role as 'teacher' | 'manager') ?? 'teacher';
  const payment = {
    id: String(base.id ?? ''),
    cycleId: String(base.cycleId ?? base.cycle_id ?? ''),
    teacherId: (base.teacherId ?? base.teacher_id ?? null) as string | null,
    managerId: (base.managerId ?? base.manager_id ?? null) as string | null,
    role,
    grossPaise: Number(base.grossPaise ?? base.gross_paise ?? 0),
    deductionsPaise: Number(base.deductionsPaise ?? base.deductions_paise ?? 0),
    netPaise: Number(base.netPaise ?? base.net_paise ?? 0),
    status: (base.status as SalaryPaymentJoined['payment']['status']) ?? 'paid',
    paidAt: (base.paidAt ?? base.paid_at ?? null) as string | null,
    paidVia: (base.paidVia ?? base.paid_via ?? null) as SalaryPaymentJoined['payment']['paidVia'],
    paidReference: (base.paidReference ?? base.paid_reference ?? null) as string | null,
    payslipNumber: (base.payslipNumber ?? base.payslip_number ?? null) as string | null,
    notes: (base.notes ?? null) as string | null,
    approvedBy: (base.approvedBy ?? base.approved_by ?? null) as string | null,
    createdAt: String(base.createdAt ?? base.created_at ?? ''),
    updatedAt: String(base.updatedAt ?? base.updated_at ?? ''),
  };
  return {
    payment,
    payee: {
      id: role === 'teacher' ? payment.teacherId ?? '' : payment.managerId ?? '',
      name: String(base.payeeName ?? base.payee_name ?? '—'),
      role,
    },
  };
}
